import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { usePageTransition } from '../../utils/pageTransitions';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { quoteService } from '../../services/quoteService';
import { projectService } from '../../services/projectService';
import LoginModal from '../administrative-dashboard/components/LoginModal';

const ClientPortalDashboard = () => {
  const { user, userProfile, signOut, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const { variants, transition } = usePageTransition();
  
  // Data State
  const [quotes, setQuotes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({
    activeQuotes: 0,
    activeProjects: 0,
    completedProjects: 0,
    totalInvoices: 0,
  });

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        setShowLoginModal(true);
      } else {
        setShowLoginModal(false);
        loadData();
      }
    }
  }, [user, authLoading]);
  
  useEffect(() => {
    if (!user) return;
    
    // Subscribe to real-time quote changes
    const unsubscribeQuotes = quoteService?.subscribeToChanges((payload) => {
      if (payload?.eventType === 'INSERT') {
        setQuotes(prev => [payload?.new, ...prev]);
        setStats(prev => ({
          ...prev,
          activeQuotes: prev?.activeQuotes + (payload?.new?.status === 'sent' || payload?.new?.status === 'pending' ? 1 : 0)
        }));
      } else if (payload?.eventType === 'UPDATE') {
        setQuotes(prev => prev?.map(q => q?.id === payload?.new?.id ? payload?.new : q));
        loadData(); // Reload to recalculate stats
      } else if (payload?.eventType === 'DELETE') {
        setQuotes(prev => prev?.filter(q => q?.id !== payload?.old?.id));
        loadData(); // Reload to recalculate stats
      }
    });
    
    // Subscribe to real-time project changes
    const unsubscribeProjects = projectService?.subscribeToChanges((payload) => {
      if (payload?.eventType === 'INSERT') {
        setProjects(prev => [payload?.new, ...prev]);
        setStats(prev => ({
          ...prev,
          activeProjects: prev?.activeProjects + (payload?.new?.status === 'in_progress' ? 1 : 0),
          totalInvoices: prev?.totalInvoices + 1
        }));
      } else if (payload?.eventType === 'UPDATE') {
        setProjects(prev => prev?.map(p => p?.id === payload?.new?.id ? payload?.new : p));
        loadData(); // Reload to recalculate stats
      } else if (payload?.eventType === 'DELETE') {
        setProjects(prev => prev?.filter(p => p?.id !== payload?.old?.id));
        loadData(); // Reload to recalculate stats
      }
    });
    
    return () => {
      unsubscribeQuotes?.();
      unsubscribeProjects?.();
    };
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [quotesData, projectsData] = await Promise.all([
        quoteService?.getAll(),
        projectService?.getAll(),
      ]);
      
      setQuotes(quotesData || []);
      setProjects(projectsData || []);
      
      // Calculate stats
      setStats({
        activeQuotes: quotesData?.filter(q => q?.status === 'sent' || q?.status === 'pending')?.length || 0,
        activeProjects: projectsData?.filter(p => p?.status === 'in_progress')?.length || 0,
        completedProjects: projectsData?.filter(p => p?.status === 'completed')?.length || 0,
        totalInvoices: projectsData?.length || 0,
      });
    } catch (error) {
      console.error('Error loading client portal data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'var(--color-muted-foreground)',
      pending: 'var(--color-warning)',
      sent: 'var(--color-trust)',
      accepted: 'var(--color-success)',
      rejected: 'var(--color-error)',
      expired: 'var(--color-muted-foreground)',
      planning: 'var(--color-trust)',
      in_progress: 'var(--color-primary)',
      on_hold: 'var(--color-warning)',
      completed: 'var(--color-success)',
      cancelled: 'var(--color-error)',
    };
    return colors?.[status] || 'var(--color-muted-foreground)';
  };

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Brouillon',
      pending: 'En attente',
      sent: 'Envoyé',
      accepted: 'Accepté',
      rejected: 'Rejeté',
      expired: 'Expiré',
      planning: 'Planification',
      in_progress: 'En cours',
      on_hold: 'En pause',
      completed: 'Terminé',
      cancelled: 'Annulé',
    };
    return labels?.[status] || status;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Chargement de votre portail...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <motion.div 
        className="flex-1 pt-28 pb-8 md:pb-12"
        initial="initial"
        animate="animate"
        exit="exit"
        variants={variants}
        transition={transition}
      >
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                  Portail Client ORAZEK
                </h1>
                <p className="text-muted-foreground">
                  Bienvenue {userProfile?.fullName || user?.email}
                </p>
              </div>
              <div className="flex gap-3">
                <Button onClick={() => navigate('/contact')} variant="outline">
                  <Icon name="MessageCircle" size={20} className="mr-2" />
                  Contacter le Support
                </Button>
                <Button onClick={handleLogout} variant="outline" iconName="LogOut" iconPosition="left">
                  Déconnexion
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
              <div className="bg-card rounded-xl p-4 md:p-6 border border-border hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Devis Actifs</p>
                    <p className="text-2xl md:text-3xl font-bold text-foreground">{stats?.activeQuotes}</p>
                  </div>
                  <Icon name="FileText" size={40} className="text-trust opacity-20" />
                </div>
              </div>
              <div className="bg-card rounded-xl p-4 md:p-6 border border-border hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Projets en Cours</p>
                    <p className="text-2xl md:text-3xl font-bold text-primary">{stats?.activeProjects}</p>
                  </div>
                  <Icon name="Briefcase" size={40} className="text-primary opacity-20" />
                </div>
              </div>
              <div className="bg-card rounded-xl p-4 md:p-6 border border-border hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Projets Terminés</p>
                    <p className="text-2xl md:text-3xl font-bold text-success">{stats?.completedProjects}</p>
                  </div>
                  <Icon name="CheckCircle" size={40} className="text-success opacity-20" />
                </div>
              </div>
              <div className="bg-card rounded-xl p-4 md:p-6 border border-border hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Documents</p>
                    <p className="text-2xl md:text-3xl font-bold text-foreground">{stats?.totalInvoices}</p>
                  </div>
                  <Icon name="Download" size={40} className="text-foreground opacity-20" />
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-card rounded-xl shadow-premium border border-border">
            <div className="border-b border-border">
              <div className="flex gap-2 p-3 md:p-4 overflow-x-auto">
                {[
                  { id: 'overview', label: 'Vue d\'ensemble', icon: 'LayoutDashboard' },
                  { id: 'quotes', label: 'Mes Devis', icon: 'FileText' },
                  { id: 'projects', label: 'Mes Projets', icon: 'Briefcase' },
                  { id: 'documents', label: 'Documents', icon: 'Download' },
                ]?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-3 md:px-4 py-2 text-sm md:text-base rounded-lg font-medium transition-colors whitespace-nowrap ${
                      activeTab === tab?.id
                        ? 'bg-primary text-white' :'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={tab?.icon} size={18} />
                    {tab?.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6">
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-foreground mb-4">Activité Récente</h2>
                    <div className="space-y-3">
                      {projects?.slice(0, 3)?.map((project) => (
                        <div
                          key={project?.id}
                          className="bg-muted/50 rounded-lg p-4 hover:bg-muted transition-colors cursor-pointer"
                          onClick={() => navigate('/project-status-tracker')}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold text-foreground">{project?.title}</h3>
                                <span
                                  className="px-2 py-1 rounded-full text-xs font-semibold"
                                  style={{
                                    backgroundColor: `${getStatusColor(project?.status)}15`,
                                    color: getStatusColor(project?.status),
                                  }}
                                >
                                  {getStatusLabel(project?.status)}
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                Projet #{project?.projectNumber}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-medium text-foreground">
                                {project?.progressPercentage || 0}%
                              </p>
                              <p className="text-xs text-muted-foreground">Progression</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-foreground mb-4">Devis en Attente</h2>
                    <div className="space-y-3">
                      {quotes?.filter(q => q?.status === 'sent')?.slice(0, 3)?.map((quote) => (
                        <div
                          key={quote?.id}
                          className="bg-muted/50 rounded-lg p-4 hover:bg-muted transition-colors"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h3 className="font-semibold text-foreground mb-1">
                                Devis #{quote?.quoteNumber}
                              </h3>
                              <p className="text-sm text-muted-foreground">
                                {quote?.serviceTitle}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-foreground">
                                {quote?.totalAmount?.toLocaleString()} {quote?.currency || 'USD'}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Expire: {new Date(quote?.expiresAt)?.toLocaleDateString('fr-FR')}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-primary/10 border border-primary rounded-xl p-6">
                    <div className="flex items-start gap-4">
                      <Icon name="Info" size={24} className="text-primary flex-shrink-0 mt-1" />
                      <div className="flex-1">
                        <h3 className="font-semibold text-foreground mb-2">
                          Besoin d'assistance ?
                        </h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          Notre équipe est disponible pour répondre à toutes vos questions concernant
                          vos projets, devis ou services.
                        </p>
                        <Button onClick={() => navigate('/contact')} size="sm">
                          <Icon name="MessageCircle" size={16} className="mr-2" />
                          Contacter le Support
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Quotes Tab */}
              {activeTab === 'quotes' && (
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                    <h2 className="text-xl font-bold text-foreground">Mes Devis</h2>
                    <Button onClick={() => navigate('/services-overview')}>
                      <Icon name="Plus" size={20} className="mr-2" />
                      Demander un Devis
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {quotes?.map((quote) => (
                      <div
                        key={quote?.id}
                        className="bg-muted/50 rounded-xl p-6 hover:bg-muted transition-colors"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold text-foreground">
                                Devis #{quote?.quoteNumber}
                              </h3>
                              <span
                                className="px-3 py-1 rounded-full text-xs font-semibold"
                                style={{
                                  backgroundColor: `${getStatusColor(quote?.status)}15`,
                                  color: getStatusColor(quote?.status),
                                }}
                              >
                                {getStatusLabel(quote?.status)}
                              </span>
                            </div>
                            <p className="text-muted-foreground mb-2">{quote?.serviceTitle}</p>
                            <div className="flex flex-wrap gap-4 text-sm">
                              <span className="text-muted-foreground">
                                Créé: {new Date(quote?.createdAt)?.toLocaleDateString('fr-FR')}
                              </span>
                              {quote?.expiresAt && (
                                <span className="text-muted-foreground">
                                  Expire: {new Date(quote?.expiresAt)?.toLocaleDateString('fr-FR')}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xl md:text-2xl font-bold text-foreground">
                              {quote?.totalAmount?.toLocaleString()} {quote?.currency || 'USD'}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <Button variant="outline" size="sm">
                            <Icon name="Download" size={16} className="mr-2" />
                            Télécharger PDF
                          </Button>
                          {quote?.status === 'sent' && (
                            <Button size="sm">
                              <Icon name="CheckCircle" size={16} className="mr-2" />
                              Accepter le Devis
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}

                    {quotes?.length === 0 && (
                      <div className="text-center py-12">
                        <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground mb-4">Aucun devis pour le moment</p>
                        <Button onClick={() => navigate('/services-overview')}>
                          <Icon name="Plus" size={20} className="mr-2" />
                          Demander un Devis
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Projects Tab */}
              {activeTab === 'projects' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-foreground">Mes Projets</h2>
                  </div>

                  <div className="space-y-4">
                    {projects?.map((project) => (
                      <div
                        key={project?.id}
                        className="bg-muted/50 rounded-xl p-6 hover:bg-muted transition-colors cursor-pointer"
                        onClick={() => navigate('/project-status-tracker')}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold text-foreground">
                                {project?.title}
                              </h3>
                              <span
                                className="px-3 py-1 rounded-full text-xs font-semibold"
                                style={{
                                  backgroundColor: `${getStatusColor(project?.status)}15`,
                                  color: getStatusColor(project?.status),
                                }}
                              >
                                {getStatusLabel(project?.status)}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground mb-3">
                              Projet #{project?.projectNumber}
                            </p>
                            
                            {/* Progress Bar */}
                            <div className="mb-2">
                              <div className="flex items-center justify-between text-sm mb-1">
                                <span className="text-muted-foreground">Progression</span>
                                <span className="font-semibold text-foreground">
                                  {project?.progressPercentage || 0}%
                                </span>
                              </div>
                              <div className="w-full bg-muted rounded-full h-2">
                                <div
                                  className="bg-primary rounded-full h-2 transition-all"
                                  style={{ width: `${project?.progressPercentage || 0}%` }}
                                />
                              </div>
                            </div>

                            <div className="flex gap-4 text-sm">
                              <span className="text-muted-foreground">
                                Début: {new Date(project?.startDate)?.toLocaleDateString('fr-FR')}
                              </span>
                              {project?.estimatedCompletion && (
                                <span className="text-muted-foreground">
                                  Fin prévue: {new Date(project?.estimatedCompletion)?.toLocaleDateString('fr-FR')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <Button variant="outline" size="sm">
                          <Icon name="Eye" size={16} className="mr-2" />
                          Voir les Détails
                        </Button>
                      </div>
                    ))}

                    {projects?.length === 0 && (
                      <div className="text-center py-12">
                        <Icon name="Briefcase" size={48} className="text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">Aucun projet en cours</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Documents Tab */}
              {activeTab === 'documents' && (
                <div>
                  <h2 className="text-xl font-bold text-foreground mb-6">Mes Documents</h2>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-muted/50 rounded-lg p-6 text-center">
                      <Icon name="FileText" size={40} className="text-primary mx-auto mb-3" />
                      <p className="text-2xl font-bold text-foreground mb-1">{quotes?.length}</p>
                      <p className="text-sm text-muted-foreground">Devis</p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-6 text-center">
                      <Icon name="Receipt" size={40} className="text-success mx-auto mb-3" />
                      <p className="text-2xl font-bold text-foreground mb-1">{projects?.length}</p>
                      <p className="text-sm text-muted-foreground">Factures</p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-6 text-center">
                      <Icon name="FileCheck" size={40} className="text-trust mx-auto mb-3" />
                      <p className="text-2xl font-bold text-foreground mb-1">{projects?.filter(p => p?.status === 'completed')?.length}</p>
                      <p className="text-sm text-muted-foreground">Certificats</p>
                    </div>
                  </div>

                  <div className="bg-primary/10 border border-primary rounded-xl p-6 text-center">
                    <Icon name="Download" size={48} className="text-primary mx-auto mb-4" />
                    <h3 className="font-semibold text-foreground mb-2">
                      Accès aux Documents
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      Téléchargez vos devis, factures et certificats depuis les onglets correspondants.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
      <Footer />
      {showLoginModal && (
        <LoginModal
          isOpen={showLoginModal}
          onClose={() => navigate('/')}
          onLogin={() => setShowLoginModal(false)}
        />
      )}
    </div>
  );
};

export default ClientPortalDashboard;